import {
  AlgorithmResult,
  ConvergenceData,
  EnergyAwareWeights,
  FitnessMode,
  OptimizationParameters,
  OptimizationResults,
} from '../types';
import {
  RESULT_ALGORITHM_NAMES,
  ResultAlgorithmKey,
  getResultAlgorithmKeys,
} from '../utils/algorithmRegistry';
import {
  OptimizationWorkerCompleteMessage,
  OptimizationWorkerInitMessage,
  OptimizationWorkerOutboundMessage,
  OptimizationWorkerProgressMessage,
  OptimizationWorkerRunMessage,
} from '../types/optimizationWorker';

type WorkerCallbacks = {
  onMessage: (event: MessageEvent<OptimizationWorkerOutboundMessage>) => void;
  onError: (event: ErrorEvent) => void;
};

interface ParallelRunOptions {
  matrix: number[][][];
  taskCount: number;
  serviceCount: number;
  parameters: OptimizationParameters;
  includeSflaGaBaseline: boolean;
  fitnessMode: FitnessMode;
  energyAwareWeights: EnergyAwareWeights;
  matrixSeed: number | null;
  runSeed: number;
  onAlgorithmStart?: (algorithm: string) => void;
  onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void;
  onAlgorithmComplete?: (
    algorithm: string,
    result: AlgorithmResult,
    partialResults: Partial<OptimizationResults>
  ) => void;
  shouldCancel?: () => boolean;
}

interface WorkerJob {
  algorithmKey: ResultAlgorithmKey;
  requestId: string;
}

const DEFAULT_PARALLEL_WORKERS = 3;
const LARGE_MATRIX_CELL_THRESHOLD = 8_000_000;

function createEmptyConvergenceData(): ConvergenceData {
  return {
    ga: [],
    culture: [],
    frog: [],
    sflaGa: [],
    sflaga: [],
    pso: [],
    gapso: [],
  };
}

export class ParallelOptimizationRunner {
  private activeWorkers = new Set<Worker>();

  cancel(): void {
    this.terminateWorkers();
  }

  async run({
    matrix,
    taskCount,
    serviceCount,
    parameters,
    includeSflaGaBaseline,
    fitnessMode,
    energyAwareWeights,
    matrixSeed,
    runSeed,
    onAlgorithmStart,
    onGenerationUpdate,
    onAlgorithmComplete,
    shouldCancel,
  }: ParallelRunOptions): Promise<{
    results: OptimizationResults;
    convergenceData: ConvergenceData;
  }> {
    const algorithmKeys = getResultAlgorithmKeys(includeSflaGaBaseline);
    const convergenceData = createEmptyConvergenceData();
    const partialResults: Partial<Record<ResultAlgorithmKey, AlgorithmResult>> = {};
    const jobs = algorithmKeys.map<WorkerJob>((algorithmKey, index) => ({
      algorithmKey,
      requestId: `${algorithmKey}-${runSeed}-${index}`,
    }));

    const workerCount = this.getWorkerCount(jobs.length, taskCount, serviceCount);
    const workers = await this.createWorkers(
      workerCount,
      matrix,
      taskCount,
      serviceCount,
      fitnessMode,
      energyAwareWeights,
      matrixSeed
    );

    return new Promise((resolve, reject) => {
      const callbacks = new Map<Worker, WorkerCallbacks>();
      let nextJobIndex = 0;
      let completedJobs = 0;
      let settled = false;

      const finishWithError = (error: unknown) => {
        if (settled) return;
        settled = true;
        this.detachWorkerCallbacks(callbacks);
        this.terminateWorkers();
        reject(error instanceof Error ? error : new Error(String(error)));
      };

      const finishWithResults = () => {
        if (settled) return;
        settled = true;
        this.detachWorkerCallbacks(callbacks);
        this.terminateWorkers();
        resolve({
          results: this.buildResults(
            partialResults,
            parameters,
            includeSflaGaBaseline,
            matrixSeed,
            runSeed,
            energyAwareWeights
          ),
          convergenceData,
        });
      };

      const buildPartialResults = (): Partial<OptimizationResults> => {
        const partial: Partial<OptimizationResults> = {};

        for (const key of algorithmKeys) {
          const result = partialResults[key];
          if (result) {
            partial[key] = result;
          }
        }

        return partial;
      };

      const assignNextJob = (worker: Worker) => {
        if (settled) return;

        if (shouldCancel?.()) {
          finishWithError(new Error('Optimization cancelled'));
          return;
        }

        const job = jobs[nextJobIndex++];
        if (!job) {
          return;
        }

        const algorithmName = RESULT_ALGORITHM_NAMES[job.algorithmKey];
        onAlgorithmStart?.(algorithmName);

        const runMessage: OptimizationWorkerRunMessage = {
          type: 'run',
          requestId: job.requestId,
          algorithmKey: job.algorithmKey,
          parameters,
          runSeed,
        };

        worker.postMessage(runMessage);
      };

      workers.forEach((worker) => {
        const onMessage = (event: MessageEvent<OptimizationWorkerOutboundMessage>) => {
          if (settled) return;

          if (shouldCancel?.()) {
            finishWithError(new Error('Optimization cancelled'));
            return;
          }

          switch (event.data.type) {
            case 'progress':
              this.handleProgressMessage(event.data, convergenceData, onGenerationUpdate);
              break;
            case 'complete':
              this.handleCompleteMessage(
                event.data,
                partialResults,
                onAlgorithmComplete,
                buildPartialResults()
              );
              completedJobs += 1;

              if (completedJobs >= jobs.length) {
                finishWithResults();
                return;
              }

              assignNextJob(worker);
              break;
            case 'error':
              finishWithError(
                new Error(event.data.error || 'Parallel optimization worker failed')
              );
              break;
            case 'ready':
            default:
              break;
          }
        };

        const onError = (event: ErrorEvent) => {
          finishWithError(new Error(event.message || 'Parallel optimization worker crashed'));
        };

        worker.addEventListener('message', onMessage);
        worker.addEventListener('error', onError);
        callbacks.set(worker, { onMessage, onError });
      });

      workers.forEach((worker) => {
        assignNextJob(worker);
      });
    });
  }

  private handleProgressMessage(
    message: OptimizationWorkerProgressMessage,
    convergenceData: ConvergenceData,
    onGenerationUpdate?: (algorithm: string, generation: number, fitness: number) => void
  ): void {
    convergenceData[message.algorithmKey]!.push({
      iteration: message.generation,
      fitness: message.fitness,
    });
    onGenerationUpdate?.(
      RESULT_ALGORITHM_NAMES[message.algorithmKey],
      message.generation,
      message.fitness
    );
  }

  private handleCompleteMessage(
    message: OptimizationWorkerCompleteMessage,
    partialResults: Partial<Record<ResultAlgorithmKey, AlgorithmResult>>,
    onAlgorithmComplete?: (
      algorithm: string,
      result: AlgorithmResult,
      partialResults: Partial<OptimizationResults>
    ) => void,
    currentPartialResults?: Partial<OptimizationResults>
  ): void {
    partialResults[message.algorithmKey] = message.result;
    onAlgorithmComplete?.(
      RESULT_ALGORITHM_NAMES[message.algorithmKey],
      message.result,
      currentPartialResults
        ? {
            ...currentPartialResults,
            [message.algorithmKey]: message.result,
          }
        : {
            [message.algorithmKey]: message.result,
          }
    );
  }

  private buildResults(
    partialResults: Partial<Record<ResultAlgorithmKey, AlgorithmResult>>,
    parameters: OptimizationParameters,
    includeSflaGaBaseline: boolean,
    matrixSeed: number | null,
    runSeed: number,
    energyAwareWeights: EnergyAwareWeights
  ): OptimizationResults {
    if (!partialResults.ga || !partialResults.culture || !partialResults.frog || !partialResults.sflaga || !partialResults.pso || !partialResults.gapso) {
      throw new Error('Parallel optimization did not return all required algorithm results');
    }

    return {
      ga: partialResults.ga,
      culture: partialResults.culture,
      frog: partialResults.frog,
      ...(includeSflaGaBaseline && partialResults.sflaGa ? { sflaGa: partialResults.sflaGa } : {}),
      sflaga: partialResults.sflaga,
      pso: partialResults.pso,
      gapso: partialResults.gapso,
      configuration: {
        taskCount: parameters.taskCount,
        propertyCount: parameters.qosProperties,
        initialPopulation: parameters.initialPopulation,
        iterations: parameters.iterations,
        selectionRate: parameters.selectionRate,
        crossover: parameters.crossoverRate,
        mutation: parameters.mutationRate,
        memeplexCount: parameters.memeplexCount,
        includeSflaGaBaseline,
        parallelExecution: parameters.parallelExecution ?? false,
        heavyMode: parameters.heavyMode ?? false,
        fitnessMode: parameters.fitnessMode,
        energyAwareWeights: parameters.fitnessMode === 'energy-aware' ? energyAwareWeights : undefined,
        sflagaEnhancements: parameters.sflagaEnhancements,
        parameterPolicy: parameters.parameterPolicy,
        seed: matrixSeed ?? undefined,
        runSeed,
      },
    };
  }

  private getWorkerCount(jobCount: number, taskCount: number, serviceCount: number): number {
    if (jobCount <= 1) {
      return 1;
    }

    const hardwareConcurrency =
      typeof navigator !== 'undefined' && Number.isFinite(navigator.hardwareConcurrency)
        ? navigator.hardwareConcurrency
        : 2;

    let workerCap = Math.max(
      2,
      Math.min(DEFAULT_PARALLEL_WORKERS, Math.max(2, hardwareConcurrency - 1))
    );

    const estimatedMatrixCells = taskCount * serviceCount * 9;
    if (estimatedMatrixCells >= LARGE_MATRIX_CELL_THRESHOLD) {
      workerCap = Math.min(workerCap, 2);
    }

    return Math.min(jobCount, workerCap);
  }

  private async createWorkers(
    count: number,
    matrix: number[][][],
    taskCount: number,
    serviceCount: number,
    fitnessMode: FitnessMode,
    energyAwareWeights: EnergyAwareWeights,
    matrixSeed: number | null
  ): Promise<Worker[]> {
    const workers = Array.from(
      { length: count },
      () => new Worker(new URL('../workers/optimizationWorker.ts', import.meta.url))
    );

    workers.forEach((worker) => this.activeWorkers.add(worker));

    try {
      await Promise.all(
        workers.map((worker) =>
          this.initializeWorker(
            worker,
            matrix,
            taskCount,
            serviceCount,
            fitnessMode,
            energyAwareWeights,
            matrixSeed
          )
        )
      );
    } catch (error) {
      this.terminateWorkers();
      throw error;
    }

    return workers;
  }

  private initializeWorker(
    worker: Worker,
    matrix: number[][][],
    taskCount: number,
    serviceCount: number,
    fitnessMode: FitnessMode,
    energyAwareWeights: EnergyAwareWeights,
    matrixSeed: number | null
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const onMessage = (event: MessageEvent<OptimizationWorkerOutboundMessage>) => {
        if (event.data.type === 'ready') {
          cleanup();
          resolve();
          return;
        }

        if (event.data.type === 'error') {
          cleanup();
          reject(new Error(event.data.error || 'Failed to initialize optimization worker'));
        }
      };

      const onError = (event: ErrorEvent) => {
        cleanup();
        reject(new Error(event.message || 'Failed to initialize optimization worker'));
      };

      const cleanup = () => {
        worker.removeEventListener('message', onMessage);
        worker.removeEventListener('error', onError);
      };

      worker.addEventListener('message', onMessage);
      worker.addEventListener('error', onError);

      const initMessage: OptimizationWorkerInitMessage = {
        type: 'init',
        matrix,
        taskCount,
        serviceCount,
        fitnessMode,
        energyAwareWeights,
        matrixSeed: matrixSeed ?? undefined,
      };

      worker.postMessage(initMessage);
    });
  }

  private detachWorkerCallbacks(callbacks: Map<Worker, WorkerCallbacks>): void {
    callbacks.forEach(({ onMessage, onError }, worker) => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
    });
  }

  private terminateWorkers(): void {
    this.activeWorkers.forEach((worker) => {
      worker.terminate();
    });

    this.activeWorkers.clear();
  }
}
